import { useEffect, useState } from 'react';

import { Modal } from '../../components/ui/Modal.jsx';
import { Field } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { FIELDS, profileToForm, validateProfile } from '../../lib/profileFields.js';
import { saveProfileForm, vloziZahtevoZaIzbris, loadZahteveIzbris } from '../../lib/api.js';
import { prenesiIzvoz } from '../../lib/izvoz.js';
import { useSola } from '../../sola.jsx';
import { fmtDate } from '../../lib/format.js';
import { t } from '../../i18n/index.js';

/*
  Tečajnik ureja svoje podatke.

  Doslej tega ni bilo mogoče nikjer: kar je vpisal ob registraciji, je
  ostalo za vedno — tudi tipkarska napaka v imenu ali stara telefonska.

  Številke tečajnika, plačila, teorije in zdravniškega se tu ne da
  spremeniti; to preprečuje trigger v bazi, ne ta obrazec.
*/

export function ProfileModal({ session, profile, data, onClose, onSaved, onOpenZasebnost }) {
  const { toast } = useUI();
  const { program } = useSola();
  const [form, setForm] = useState(() => profileToForm(profile));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [zahteve, setZahteve] = useState([]);
  const [prosnja, setProsnja] = useState('');
  const [posiljam, setPosiljam] = useState(false);

  useEffect(() => {
    loadZahteveIzbris(session.user.id)
      .then(setZahteve)
      .catch(() => setZahteve([]));
  }, [session.user.id]);

  const odprta = zahteve.find((z) => !z.resil);

  const set = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const n = { ...e };
      Object.keys(patch).forEach((k) => delete n[k]);
      return n;
    });
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validateProfile(form, { requireDeclaration: false });
    setErrors(e);
    if (Object.keys(e).length) {
      const first = document.querySelector('[aria-invalid="true"]');
      first?.focus();
      return;
    }

    setSaving(true);
    try {
      await saveProfileForm(session.user.id, session.user.email, {
        ...form,
        izjava_sprejeta: profile?.izjava_sprejeta ?? form.izjava_sprejeta,
      });
      toast.ok(t('Podatki so shranjeni.'));
      await onSaved();
      onClose();
    } catch (err) {
      toast.bad(err.message ?? t('Shranjevanje ni uspelo.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      eyebrow={t('Moj profil')}
      title={t('Osebni podatki')}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--primary" onClick={submit} disabled={saving}>
            {saving ? t('Shranjujem …') : t('Shrani')}
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            {t('Prekliči')}
          </button>
        </>
      }
    >
      <form className="stack" onSubmit={submit} noValidate>
        {profile?.stevilka != null && (
          <p className="card card--brand card--pad t-sm t-dim">
            {t('Tvoja številka tečajnika je')} <strong className="num t-brand">{profile.stevilka}</strong>.{' '}
            {t('Te ni mogoče spremeniti — dodeli jo šola.')}
          </p>
        )}

        {FIELDS.map((f) => (
          <Field
            key={f.key}
            id={`p-${f.key}`}
            label={t(f.label)}
            required={f.required}
            hint={f.key === 'datum_tecaja' ? t('Kdaj si dejansko začel s tečajem.') : f.hint && t(f.hint)}
            error={errors[f.key]}
          >
            {f.multi ? (
              <textarea
                id={`p-${f.key}`}
                className="input"
                rows={3}
                value={form[f.key] ?? ''}
                placeholder={f.placeholder && t(f.placeholder)}
                aria-invalid={errors[f.key] ? 'true' : undefined}
                onChange={(e) => set({ [f.key]: e.target.value })}
              />
            ) : (
              <input
                id={`p-${f.key}`}
                className="input"
                type={f.type ?? 'text'}
                value={form[f.key] ?? ''}
                placeholder={f.placeholder && t(f.placeholder)}
                autoComplete={f.autoComplete}
                aria-invalid={errors[f.key] ? 'true' : undefined}
                onChange={(e) => set({ [f.key]: e.target.value })}
              />
            )}
          </Field>
        ))}

        <hr className="hr" />

        {/* ── Tvoji podatki ──────────────────────────────────
            Pravica do dostopa, prenosljivosti in izbrisa ni izpolnjena s
            tem, da podatke nekje vidiš — moraš jih lahko odnesti in
            zahtevati izbris. */}
        <section className="stack stack--tight">
          <h3 className="t-base">{t('Tvoji podatki')}</h3>

          <button
            type="button"
            className="btn btn--secondary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              prenesiIzvoz({ profile, ...data, program });
              toast.ok(t('Podatki so preneseni (.txt in .json).'));
            }}
          >
            <Icon name="download" size={15} /> {t('Prenesi vse svoje podatke')}
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={onOpenZasebnost}
          >
            {t('Kako ravnamo s tvojimi podatki')}
          </button>

          {odprta ? (
            <p className="card card--pad t-sm t-dim">
              {t('Zahtevo za izbris si vložil {d}.', { d: fmtDate(odprta.vlozeno) })}{' '}
              {t('Inštruktor jo bo obravnaval in ti sporočil, kaj je izbrisal in kaj mora šola po zakonu obdržati.')}
            </p>
          ) : (
            <details>
              <summary className="t-sm t-dimmer" style={{ cursor: 'pointer' }}>
                {t('Zahtevaj izbris svojih podatkov')}
              </summary>
              <div className="stack stack--tight" style={{ marginTop: 'var(--s-2)' }}>
                <p className="t-sm t-dim">
                  {t('Fotografije dokumentov lahko izbrišeš takoj sam, v razdelku Dokumenti. Evidence šolanja in zaključnega testa šola ne sme izbrisati — te mora hraniti po predpisih. Vse ostalo izbriše inštruktor na tvojo zahtevo.')}
                </p>
                <textarea
                  className="input"
                  rows={2}
                  value={prosnja}
                  placeholder={t('Neobvezno: kaj konkretno želiš izbrisati.')}
                  onChange={(e) => setProsnja(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  style={{ alignSelf: 'flex-start' }}
                  disabled={posiljam}
                  onClick={async () => {
                    setPosiljam(true);
                    try {
                      await vloziZahtevoZaIzbris(session.user.id, prosnja);
                      setZahteve(await loadZahteveIzbris(session.user.id));
                      setProsnja('');
                      toast.ok(t('Zahteva je vložena. Inštruktor te bo obvestil.'));
                    } catch (e) {
                      toast.bad(e.message ?? t('Zahteve ni bilo mogoče vložiti.'));
                    } finally {
                      setPosiljam(false);
                    }
                  }}
                >
                  {posiljam ? t('Pošiljam …') : t('Vloži zahtevo')}
                </button>
              </div>
            </details>
          )}
        </section>
      </form>
    </Modal>
  );
}
